import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import {
  AccountPublicApiService
} from '@/app/platform/platform-core/user/services/account-public-api.service';
import {
  isPasswordAcceptable,
  MINIMUM_PASSWORD_LENGTH,
  PasswordStrengthMeterComponent
} from '@/app/platform/platform-core/user/components/password-strength-meter/password-strength-meter.component';
import { PublicAccountShellComponent } from '../public-account-shell/public-account-shell.component';
import { privacyStatementUrl } from '../legal-page/privacy-statement';
import { EgiErrorCode } from '@data/_generated-api/error-mappers/egi-error-code';
import { ErrorInformation } from '@shared/models/error/error-information';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Publieke registratiepagina met registratiecode-ondersteuning en sterkte-meter. */
@Component({
  selector: 'register-page',
  imports: [
    FormsModule,
    RouterLink,
    TranslocoModule,
    SharedModule,
    PublicAccountShellComponent,
    PasswordStrengthMeterComponent
  ],
  templateUrl: './register-page.component.html',
  styleUrl: './register-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterPageComponent implements OnInit {
  private readonly accountApi = inject(AccountPublicApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly transloco = inject(TranslocoService);

  protected readonly minimumPasswordLength = MINIMUM_PASSWORD_LENGTH;

  /** Centrale privacyverklaring op de landing, in de taal die de taalkeuze hier zet. */
  private readonly activeLanguage = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang()
  });
  protected readonly privacyUrl = computed(() => privacyStatementUrl(this.activeLanguage()));

  protected readonly registrationEnabled = signal<boolean | null>(null);
  protected readonly isSubmitting = signal(false);
  protected readonly isResending = signal(false);
  protected readonly registrationCompleted = signal(false);
  protected readonly resendCompleted = signal(false);
  protected readonly errorKey = signal<string | null>(null);

  protected readonly firstName = signal('');
  protected readonly lastName = signal('');
  protected readonly email = signal('');
  protected readonly password = signal('');
  protected readonly confirmPassword = signal('');
  protected readonly acceptedTerms = signal(false);
  protected readonly registrationCode = signal('');

  protected readonly emailIsValid = computed(() => EMAIL_PATTERN.test(this.email().trim()));
  protected readonly passwordIsAcceptable = computed(() => isPasswordAcceptable(this.password()));
  protected readonly passwordsMatch = computed(
    () => this.confirmPassword() === '' || this.password() === this.confirmPassword()
  );

  /** Registratiecode is verplicht wanneer publieke registratie gesloten is. */
  protected readonly codeIsRequired = computed(() => this.registrationEnabled() === false);

  protected readonly canSubmit = computed(
    () =>
      !this.isSubmitting() &&
      this.firstName().trim() !== '' &&
      this.lastName().trim() !== '' &&
      this.emailIsValid() &&
      this.passwordIsAcceptable() &&
      this.password() === this.confirmPassword() &&
      this.acceptedTerms() &&
      (!this.codeIsRequired() || this.registrationCode().trim() !== '')
  );

  ngOnInit(): void {
    const codeFromUrl = this.route.snapshot.queryParamMap.get('code');
    if (codeFromUrl) {
      this.registrationCode.set(codeFromUrl);
    }
    this.loadRegistrationConfig();
  }

  async onSubmit(): Promise<void> {
    if (!this.canSubmit()) {
      return;
    }

    this.errorKey.set(null);
    this.isSubmitting.set(true);
    const result = await this.accountApi.register({
      firstName: this.firstName().trim(),
      lastName: this.lastName().trim(),
      email: this.email().trim(),
      password: this.password(),
      confirmPassword: this.confirmPassword(),
      acceptedTerms: this.acceptedTerms(),
      registrationCode: this.registrationCode().trim() || null,
      language: this.transloco.getActiveLang()
    });
    this.isSubmitting.set(false);

    result.ifSuccess(() => this.registrationCompleted.set(true));
    result.ifError(errors => this.errorKey.set(this.toErrorKey(errors)));
  }

  async onResendVerification(): Promise<void> {
    this.errorKey.set(null);
    this.isResending.set(true);
    const result = await this.accountApi.resendVerification(
      this.email().trim(),
      this.transloco.getActiveLang()
    );
    this.isResending.set(false);
    result.ifSuccess(() => this.resendCompleted.set(true));
    result.ifError(errors => this.errorKey.set(this.toErrorKey(errors)));
  }

  private async loadRegistrationConfig(): Promise<void> {
    const result = await this.accountApi.isRegistrationEnabled();
    result.ifSuccess(enabled => this.registrationEnabled.set(enabled));
    result.ifError(() => this.registrationEnabled.set(false));
  }

  /** Titel boven de foutmelding; alleen gevuld bij een mailservice-storing. */
  protected readonly errorTitleKey = computed(() =>
    this.errorKey() === 'account.mailServiceUnavailable.text'
      ? 'account.mailServiceUnavailable.title'
      : null
  );

  private toErrorKey(errors: ErrorInformation[]): string {
    const codes = errors.map(error => error.errorCode);
    if (codes.includes(EgiErrorCode.EmailServiceUnavailable)) {
      return 'account.mailServiceUnavailable.text';
    }
    if (codes.includes(EgiErrorCode.RegistrationKeyInvalid)) {
      return 'account.register.errors.registrationKeyInvalid';
    }
    if (codes.includes(EgiErrorCode.RegistrationClosed)) {
      return 'account.register.errors.registrationClosed';
    }
    if (codes.includes(EgiErrorCode.RegistrationNotPossible)) {
      return 'account.register.errors.registrationNotPossible';
    }
    return 'account.register.errors.generic';
  }
}
